// C5 — the NL decomposer LLM's raw JSON output must never select a model/checkpoint;
// that decision belongs entirely to the deterministic router (modelRouter.js). This
// module whitelists generation_spec.meta to the keys the LLM (or AppProvider itself)
// legitimately sets in the NL path, and validates the decomposer's overall response
// shape before AppProvider ever touches .generation_spec.

// Keys legitimately present in generation_spec.meta after the full NL submit
// pipeline: user_prompt_raw/application/assumptions may come from the decomposer LLM;
// pre_classified_domains is set by AppProvider itself (Stage 1 classifier/chip/keyword
// fallback, never the LLM); resolved_domains/resolution_method/resolution_notes are
// written by resolveSpec() AFTER sanitizeNlMeta runs (see AppProvider.jsx) — they're
// listed here so this constant stays a complete description of the final meta shape,
// even though sanitizeNlMeta itself never receives them as input at its one call site.
export const SAFE_NL_META_KEYS = [
  'user_prompt_raw',
  'application',
  'assumptions',
  'pre_classified_domains',
  'resolved_domains',
  'resolution_method',
  'resolution_notes',
];

/** Rebuild a generation_spec.meta object containing only SAFE_NL_META_KEYS. Drops any
 * LLM-emitted mattergen_options/fm4m_options/gt4sd_options/model_path/seed_smiles/etc
 * — the override vector chained with I1 into direct checkpoint/model selection. Pure;
 * does not mutate its input. */
export function sanitizeNlMeta(meta) {
  const out = {};
  if (!meta || typeof meta !== 'object') return out;
  for (const key of SAFE_NL_META_KEYS) {
    if (Object.prototype.hasOwnProperty.call(meta, key)) out[key] = meta[key];
  }
  return out;
}

/** Normalize the decomposer LLM's parsed JSON into the shape AppProvider needs before
 * touching .generation_spec: unwrap a single-element array (some models wrap their
 * JSON output in an array), and return null for anything that isn't a plain,
 * non-array object — the caller then shows the clean "Model did not return valid
 * JSON" error instead of crashing on a TypeError deeper in the pipeline. */
export function normalizeDecomposerOutput(parsed) {
  let p = parsed;
  if (Array.isArray(p)) {
    if (p.length !== 1) return null;
    p = p[0];
  }
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  return p;
}
